import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Text,
  Flex,
  useColorMode,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import api from "../config/axios";

export default function CsvUploader() {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [csvFileName, setCsvFileName] = useState(null);
  const [showUploader, setShowUploader] = useState(false); // State to toggle visibility
  const { colorMode } = useColorMode();

  // Dynamic styles based on color mode
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBgColor = useColorModeValue("gray.50", "gray.700");

  // Handle file input selection
  const handleFileUpload = (event) => {
    setFile(event.target.files[0]);
  };

  // Handle cell data change
  const handleCellChange = (rowIndex, colKey, value) => {
    const updatedData = [...editedData];
    updatedData[rowIndex][colKey] = value;
    setEditedData(updatedData);
  };

  // Check if a column should be displayed based on the row data
  const shouldDisplayColumn = (colKey, row) => {
    // Hide answer column completely for MRI questions, even if it has an empty string
    if (colKey === "answer" && row.question_type === "mri") {
      return false;
    }

    // For columns that should be conditionally hidden based on content
    if (
      (row.question_type === "fill_blanks" ||
        row.question_type === "descriptive") &&
      (colKey.startsWith("option_") || colKey.startsWith("mri_answer_"))
    ) {
      // Only show if the value is not empty
      return row[colKey] && row[colKey].trim() !== "";
    }

    return true;
  };

  // Upload file and fetch CSV data from the server
  const uploadFile = async () => {
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Using the new API endpoint
      const response = await api.post(
        "/convert_to_structured_format",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Extract file name from the path
      const fullPath = response.data.csv_file_path;
      const fileName = fullPath.split("/").pop();
      setCsvFileName(fileName);

      // If we need to show a preview immediately, we can use the sample_data
      if (response.data.sample_data) {
        // Show loading text
        setCsvData({ csv_data: response.data.sample_data });
        setEditedData(response.data.sample_data);
      }

      try {
        // Fetch the full CSV data
        const csvResponse = await api.get(
          `/display_csv?csv_file_name=${fileName}`
        );

        // Process the response
        if (csvResponse.data && csvResponse.data.csv_data) {
          // Standard response format
          setCsvData(csvResponse.data);
          setEditedData(csvResponse.data.csv_data);
        } else {
          // Direct data format (if csv_data is not wrapped)
          setCsvData({ csv_data: csvResponse.data });
          setEditedData(csvResponse.data);
        }
      } catch (csvError) {
        console.error("Error fetching complete CSV data:", csvError);
        // If we can't get the full data, at least we have the sample data
        if (response.data.sample_data) {
          alert("Could not fetch complete data. Using sample data instead.");
        } else {
          alert("Error fetching data from the server.");
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error uploading file or fetching CSV data:", error);
      alert("Error processing the file. Please try again.");
      setIsLoading(false);
    }
  };

const generateCsv = () => {
  if (!editedData || editedData.length === 0) {
    alert("No data to download");
    return;
  }

  // Get all headers
  const headers = getUniqueColumns();

  // Create CSV content with all headers, fix "question" to "questions" in the header
  const csvHeaders = headers.map(header => header === "question" ? "questions" : header);
  
  const csvContent = [
    csvHeaders.join(","),
    ...editedData.map((row) =>
      headers
        .map((header) => {
          // For MRI questions, ensure answer field is empty
          if (row.question_type === "mri" && header === "answer") {
            return '""';
          }

          // Handle null, undefined, or empty values
          const value =
            row[header] !== null && row[header] !== undefined
              ? row[header]
              : "";

          // Wrap ALL text fields in quotes to handle commas, line breaks, and special characters
          return typeof value === "string"
            ? `"${value.replace(/"/g, '""')}"` // Escape quotes by doubling them
            : value;
        })
        .join(",")
    ),
  ].join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", "questions.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  // Get unique columns from all rows to account for varying structures in MRI vs MCQ questions
  const getUniqueColumns = () => {
    if (!editedData || editedData.length === 0) return [];

    const allColumns = new Set();
    editedData.forEach((row) => {
      Object.keys(row).forEach((key) => allColumns.add(key));
    });

    return Array.from(allColumns);
  };

  return (
    <Box>
      {/* Button to toggle CsvUploader visibility */}
      <Flex justifyContent="flex-end" mb={4}>
        <Button
          onClick={() => setShowUploader(!showUploader)}
          colorScheme="blue"
        >
          {showUploader ? "Hide CSV Converter" : "Open CSV Converter"}
        </Button>
      </Flex>

      {/* Use Modal for better UI containment */}
      <Modal
        isOpen={showUploader}
        onClose={() => setShowUploader(false)}
        size="full"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent bg={bgColor} color={textColor}>
          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            CSV Converter
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={4}>
            <VStack spacing={4} align="stretch">
              {/* File input field */}
              <Flex direction={{ base: "column", md: "row" }} gap={4}>
                <Input
                  type="file"
                  onChange={handleFileUpload}
                  bg={useColorModeValue("white", "gray.700")}
                  border="1px"
                  borderColor={borderColor}
                  p={2}
                  flex={1}
                />
                <Button
                  onClick={uploadFile}
                  colorScheme="teal"
                  isLoading={isLoading}
                  isDisabled={!file}
                  width={{ base: "full", md: "auto" }}
                >
                  Upload and Convert
                </Button>
              </Flex>

              {/* Display editable CSV data in table */}
              {csvData && editedData && editedData.length > 0 ? (
                <Box
                  mt={4}
                  overflowX="auto"
                  maxH="70vh"
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                >
                  <Table
                    variant="simple"
                    size="sm"
                    style={{ tableLayout: "fixed" }}
                  >
                    <Thead
                      position="sticky"
                      top={0}
                      zIndex={1}
                      bg={headerBgColor}
                    >
                      <Tr>
                        {getUniqueColumns().map((header, idx) => (
                          <Th
                            key={idx}
                            fontSize="xs"
                            py={3}
                            width={
                              header === "question"
                                ? "250px"
                                : header === "answer"
                                ? "200px"
                                : header === "option_1" ||
                                  header === "option_2" ||
                                  header === "option_3" ||
                                  header === "option_4"
                                ? "150px"
                                : "120px"
                            }
                            sx={{ fontFamily: "'Open Sans', sans-serif" }}
                          >
                            {header === "question" ? "questions" : header}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>

                    <Tbody>
                      {editedData.map((row, rowIndex) => (
                        <Tr key={rowIndex}>
                          {getUniqueColumns().map((colKey, colIndex) => {
                            // For MRI questions, don't show the answer cell at all
                            if (
                              colKey === "answer" &&
                              row.question_type === "mri"
                            ) {
                              return (
                                <Td key={colIndex} px={2} py={1}>
                                  {/* Empty cell for MRI answer */}
                                </Td>
                              );
                            }

                            // Skip rendering cells for columns that should be hidden for other reasons
                            if (
                              !shouldDisplayColumn(colKey, row) &&
                              !(
                                colKey === "answer" &&
                                row.question_type === "mri"
                              )
                            ) {
                              return null;
                            }

                            // Customize input based on column type
                            const isLongText =
                              colKey === "question" ||
                              colKey === "answer" ||
                              colKey.startsWith("option_") ||
                              colKey.startsWith("mri_answer_");

                            return (
                              <Td key={colIndex} px={2} py={1}>
                                {isLongText ? (
                                  <Input
                                    as="textarea"
                                    value={
                                      row[colKey] !== null &&
                                      row[colKey] !== undefined
                                        ? row[colKey]
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleCellChange(
                                        rowIndex,
                                        colKey,
                                        e.target.value
                                      )
                                    }
                                    size="xs"
                                    minH={
                                      colKey === "question" ? "80px" : "40px"
                                    }
                                    minW="100px"
                                    resize="vertical"
                                    bg={useColorModeValue("white", "gray.700")}
                                    _focus={{
                                      borderColor: "blue.500",
                                      boxShadow: "0 0 0 1px blue.500",
                                    }}
                                    isReadOnly={
                                      colKey === "answer" &&
                                      row.question_type === "mri"
                                    }
                                  />
                                ) : (
                                  <Input
                                    value={
                                      row[colKey] !== null &&
                                      row[colKey] !== undefined
                                        ? row[colKey]
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleCellChange(
                                        rowIndex,
                                        colKey,
                                        e.target.value
                                      )
                                    }
                                    size="xs"
                                    minW="100px"
                                    bg={useColorModeValue("white", "gray.700")}
                                    _focus={{
                                      borderColor: "blue.500",
                                      boxShadow: "0 0 0 1px blue.500",
                                    }}
                                    isReadOnly={
                                      colKey === "answer" &&
                                      row.question_type === "mri"
                                    }
                                  />
                                )}
                              </Td>
                            );
                          })}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                !isLoading && (
                  <Text mt={4}>
                    No data available. Please upload a file to get started.
                  </Text>
                )
              )}

              {/* Download CSV button */}
              {csvData && editedData && editedData.length > 0 && (
                <Button
                  mt={4}
                  colorScheme="blue"
                  onClick={generateCsv}
                  alignSelf="flex-start"
                >
                  Download CSV
                </Button>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
